import { MediaType } from '@confluence/graphql/entry-points/MediaType.ts';
import { useExperimentalMutation } from '@confluence/legacy-schema-bridge/entry-points/useExperimentalMutation.ts';
import { getEditDraftPageUrl } from '@confluence/graphql/entry-points/sharedTemplateBlueprintReqFns.ts';

import type {
	CreatePageByTemplateMutation as PublishPageByTemplateMutation,
	CreatePageByTemplateMutationVariables as PublishPageByTemplateMutationVariables,
} from './__types__/CreatePageByTemplateMutation.ts';

export const useExperimentalPublishPageByTemplateMutation = () => {
	return useExperimentalMutation<
		PublishPageByTemplateMutation,
		PublishPageByTemplateMutationVariables,
		PublishPageByTemplateMutationResponse
	>({
		url: ({ blueprint, attemptSpaUrlResponse }) => {
			const blueprintSpec = JSON.parse(blueprint);
			return `/wiki/rest/api/template/page/${blueprintSpec.templateId}/instance${
				attemptSpaUrlResponse ? '?expand=metadata.properties.editor,space' : ''
			}`;
		},
		method: 'POST',
		headers: {
			'Content-Type': MediaType.JSON,
		},
		body: ({ spaceKey, parentPageId: contentId, blueprint, title, subType }) => {
			const blueprintSpec = JSON.parse(blueprint);

			const commonContentProperties: any = {
				type: 'page',
				status: 'draft',
				space: {
					key: spaceKey,
				},
				title,
			};

			if (contentId && contentId !== 'no-parent-page') {
				commonContentProperties.ancestors = [{ id: contentId }];
			}

			if (subType) {
				commonContentProperties.subType = subType;
			}

			return JSON.stringify({
				content: {
					...commonContentProperties,
				},
				contentBlueprintSpec: {
					contentTemplateId: blueprintSpec.templateId,
				},
			});
		},
		parseResult: async (json, { attemptSpaUrlResponse }) => {
			const editui = await getEditDraftPageUrl(json, attemptSpaUrlResponse);

			if (!editui) {
				throw new Error('Missing content draft url');
			}

			return {
				experimentalPublishPageByTemplate: {
					links: {
						draftUrl: editui,
					},
				},
			};
		},
	});
};

interface PublishPageByTemplateMutationResponse {
	_links?: {
		editui?: string;
	};
	metadata?: {
		properties?: {
			editor?: {
				value?: string;
			};
		};
	};
}
